/**
 * Doserne Academy — "My certificates" page.
 * Lists every certificate the current user has been issued: course title,
 * issue date, score, version, plus buttons for View PDF and View signed
 * acknowledgement.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Award, Download, Eye, FileSignature, Loader2 } from "lucide-react";
import DoserneShell from "./DoserneShell";
import { useLang, pick } from "./i18n";
import { useDoserneAuth } from "./doserneAuth";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const L = {
  no: {
    title: "Mine kursbevis",
    lead: "Alle kursbevis du er tildelt fra Doserne Academy.",
    empty: "Du har ingen kursbevis ennå. Fullfør et kurs og signer bekreftelsen for å motta det første.",
    issued: "Utstedt",
    score: "Resultat",
    version: "Versjon",
    viewPdf: "Vis PDF",
    viewAck: "Vis signert bekreftelse",
    superseded: "Erstattet av nyere versjon",
    passedNeedsSign: "Bestått — signer for å motta",
  },
  ro: {
    title: "Diplomele mele",
    lead: "Toate certificatele emise de Doserne Academy pentru contul tău.",
    empty: "Nu ai niciun certificat. Finalizează un curs și semnează confirmarea pentru a primi primul.",
    issued: "Emis",
    score: "Rezultat",
    version: "Versiune",
    viewPdf: "Vezi PDF",
    viewAck: "Vezi confirmarea semnată",
    superseded: "Înlocuit de versiune nouă",
    passedNeedsSign: "Promovat — semnează pentru a primi",
  },
  en: {
    title: "My certificates",
    lead: "All certificates issued to you by Doserne Academy.",
    empty: "You have no certificates yet. Complete a course and sign the acknowledgement to receive the first one.",
    issued: "Issued",
    score: "Result",
    version: "Version",
    viewPdf: "View PDF",
    viewAck: "View signed acknowledgement",
    superseded: "Superseded by a newer version",
    passedNeedsSign: "Passed — sign to receive",
  },
};

export default function DoserneMyCertificates() {
  const { lang } = useLang();
  const { authHeader } = useDoserneAuth();
  const t = L[lang] || L.no;
  const [certs, setCerts] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/certificates/mine`, authHeader());
        setCerts(data.certificates || []);
      } catch (e) {
        setError("load");
        setCerts([]);
      }
    })();
  }, [authHeader]);

  return (
    <DoserneShell>
      <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="my-certificates-page">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900">{t.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{t.lead}</p>
        </div>

        {certs === null && (
          <div className="p-8 text-slate-500 flex justify-center"><Loader2 className="animate-spin" /></div>
        )}

        {certs && certs.length === 0 && (
          <div className="p-8 rounded-3xl border-2 border-dashed border-slate-200 bg-white text-center text-slate-500" data-testid="my-certificates-empty">
            {t.empty}
          </div>
        )}

        {certs && certs.length > 0 && (
          <div className="space-y-4">
            {certs.map((c) => (
              <CertificateCard key={c.id} cert={c} t={t} lang={lang} />
            ))}
          </div>
        )}
      </div>
    </DoserneShell>
  );
}

function CertificateCard({ cert, t, lang }) {
  const issued = cert.issued_at ? new Date(cert.issued_at).toLocaleDateString(lang === "en" ? "en-GB" : lang === "ro" ? "ro-RO" : "no-NO") : "—";
  const scorePct = Math.round((cert.score || 0) * 100);
  const isSuperseded = !!cert.superseded_by;

  return (
    <div className={"rounded-3xl bg-white border shadow-sm p-5 sm:p-6 flex flex-col sm:flex-row gap-4 " + (isSuperseded ? "border-amber-200" : "border-slate-200")} data-testid="cert-card">
      <div className="flex-none">
        <div className="w-14 h-14 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
          <Award className="w-7 h-7" />
        </div>
      </div>
      <div className="flex-1">
        <div className="text-xs font-mono text-slate-400 mb-1">{cert.cert_number}</div>
        <div className="text-lg font-black text-slate-900 leading-tight">{pick(cert.course_title, lang)}</div>
        {isSuperseded && (
          <div className="mt-1 inline-block text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 uppercase tracking-widest">
            {t.superseded}
          </div>
        )}
        <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
          <MetaCell label={t.issued} value={issued} />
          <MetaCell label={t.score} value={`${scorePct}%`} mono />
          <MetaCell label={t.version} value={cert.course_version || "—"} mono />
        </div>
      </div>
      <div className="flex flex-col gap-2 justify-center sm:min-w-[220px]">
        <a
          href={`${API}/doserne/certificates/${cert.id}/pdf`}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold"
          data-testid="cert-card-view-pdf"
        >
          <Eye className="w-4 h-4" /> {t.viewPdf}
        </a>
        {cert.ack_id && (
          <Link
            to={`/doserne/kurs/${cert.course_code}/sign`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-900 text-sm font-bold"
            data-testid="cert-card-view-ack"
          >
            <FileSignature className="w-4 h-4" /> {t.viewAck}
          </Link>
        )}
      </div>
    </div>
  );
}

function MetaCell({ label, value, mono }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{label}</span>
      <span className={"text-sm font-bold text-slate-900 " + (mono ? "font-mono" : "")}>{value}</span>
    </div>
  );
}
